import { PageSize, SortDirection } from '@/constants/enum';
import { ShipmentStatus } from '@/constants/shipment';
import { getSomeDaysAgo } from '@/helpers';
import { EShipmentType } from '@/pages/Shipments/config';
import { IDatePicker } from '@/types';
import { ITrackingDetail } from '@/types/shipment';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';

interface IShipmentFilter {
  statusIndex: number;
  status: ShipmentStatus;
  search: string;
  couriers: string[];
  statusIn: string[];
  page: number;
  pageSize: PageSize;
  sortBy: string;
  sortOrder: SortDirection;
  type: EShipmentType;
}

interface IDisabledResyncBtn {
  time: Date;
  tooltip: string;
  detail: {
    id: string;
    time: Date;
  }[];
  detailTooltip: string;
}

export interface ICourierModal {
  open: boolean;
  value: string;
  id: Array<number>;
}

export interface IStatusModal {
  open: boolean;
  orderId: string;
  trackingNumber: string;
  status: ShipmentStatus;
  history: ITrackingDetail[];
  isSend: boolean;
}

export interface IShipmentState {
  filter: IShipmentFilter;
  titleDatePicker: string;
  datePicker: IDatePicker;
  disabledBtn: IDisabledResyncBtn;
  courierModal: ICourierModal;
  statusModal: IStatusModal;
}

export const initialStateFilter: IShipmentFilter = {
  statusIndex: 0,
  status: ShipmentStatus.All,
  search: '',
  couriers: [],
  statusIn: [],
  page: 1,
  pageSize: PageSize.TwentyFive,
  sortOrder: SortDirection.DESC,
  sortBy: 'createdAt',
  type: EShipmentType.TRACKED_SHIPMENT,
};

const initialState: IShipmentState = {
  filter: { ...initialStateFilter },
  datePicker: {
    start: getSomeDaysAgo(30),
    end: new Date(),
  },
  titleDatePicker: 'Current cycle',
  disabledBtn: {
    time: new Date(),
    tooltip: '',
    detail: [],
    detailTooltip: '',
  },

  courierModal: {
    open: false,
    value: '',
    id: [],
  },
  statusModal: {
    open: false,
    orderId: '',
    trackingNumber: '',
    status: ShipmentStatus.CourierNotSupported,
    history: [],
    isSend: false,
  },
};

export const shipmentSlice = createSlice({
  name: 'shipment',
  initialState,
  reducers: {
    handleChangeTableFilter: (state: IShipmentState, action: PayloadAction<IShipmentFilter>) => {
      state.filter = action.payload;
    },
    handleDatePicker: (state, action: PayloadAction<IDatePicker>) => {
      state.datePicker = action.payload;
    },
    handleTitleDatePicker: (state, action: PayloadAction<string>) => {
      state.titleDatePicker = action.payload;
    },
    handleSetDisabledBtnTime: (state, action: PayloadAction<IDisabledResyncBtn>) => {
      state.disabledBtn = action.payload;
    },
    handleChangeCourierModal: (state, action: PayloadAction<ICourierModal>) => {
      state.courierModal = action.payload;
    },
    handleChangeStatusModal: (state, action: PayloadAction<IStatusModal>) => {
      state.statusModal = action.payload;
    },
  },
});

export const shipmentFilterSelector = createSelector(
  (state: RootState) => state.shipment,
  (state) => state.filter,
);
export const datePickerSelector = createSelector(
  (state: RootState) => state.shipment,
  (state) => state.datePicker,
);
export const titleDatePickerSelector = createSelector(
  (state: RootState) => state.shipment,
  (state) => state.titleDatePicker,
);
export const disabledBtnSelector = createSelector(
  (state: RootState) => state.shipment,
  (state) => state.disabledBtn,
);
export const courierModalSelector = createSelector(
  (state: RootState) => state.shipment,
  (state) => state.courierModal,
);
export const statusModalSelector = createSelector(
  (state: RootState) => state.shipment,
  (state) => state.statusModal,
);

export default shipmentSlice;
